import {
  Box,
  Button,
  Card,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { H3, H5 } from 'components/Typography';

const SubscriptionPlansCard = ({ ...props }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Sample subscription plans data
  const subscriptionPlans = [
    {
      id: 1,
      title: '프리미엄 지원사업 알림 서비스',
      tag: '프리미엄',
      tagColor: '#34D399',
      price: '월 9,900원',
      nextPayment: '다음 결제: 2025.10.31',
    },
    {
      id: 2,
      title: '맞춤형 지원금 추천',
      tag: '베이직',
      tagColor: '#F59E0B',
      price: '월 4,900원',
      nextPayment: '다음 결제: 2025.10.31',
    },
    {
      id: 3,
      title: '커뮤니티 VIP 멤버쉽',
      tag: 'VIP',
      tagColor: '#A78BFA',
      price: '월 19,900원',
      nextPayment: '다음 결제: 2025.10.31',
    },
  ];

  const handlePlanChange = planId => {
    console.log('Change plan:', planId);
    // Add your plan change logic here
  };

  const handleUnsubscribe = planId => {
    console.log('Unsubscribe from plan:', planId);
    // Add your unsubscribe logic here
  };

  const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    backgroundColor: theme.palette.primary.lightGrey200,
    borderRadius: 0,
    border: 'none',
  }));

  const PlanCard = styled(Box)(({ theme }) => ({
    backgroundColor: '#F8F8F8',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }));

  const PlanContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  }));

  const PlanInfoSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
  }));

  const PlanTitle = styled(H3)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    lineHeight: '1.4',
  }));

  const PlanDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  }));

  const PlanTag = styled(Box)(({ theme, tagColor }) => ({
    backgroundColor: tagColor,
    color: '#FFFFFF',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
  }));

  const PlanInfo = styled(Box)(({ theme }) => ({
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: '400',
  }));

  const ActionButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '8px',
    [theme.breakpoints.up('lg')]: {
      marginTop: '0px',
      justifyContent: 'flex-end',
    },
  }));

  const ChangePlanButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    minWidth: '80px',
    '&:hover': {
      backgroundColor: '#2563EB',
    },
  }));

  const UnsubscribeButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#E5E7EB',
    color: '#6B7280',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    minWidth: '80px',
    '&:hover': {
      backgroundColor: '#D1D5DB',
    },
  }));

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Title */}
          <Box
            sx={{
              marginBottom: '24px',
            }}
          >
            <H5
              sx={{
                fontWeight: 'bold',
                lineHeight: 1.3,
                fontSize: isMobile ? '16px' : '18px',
              }}
            >
              현재 구독 플랜
            </H5>
          </Box>

          {/* Subscription Plans */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: 1,
            }}
          >
            {subscriptionPlans.map(plan => (
              <PlanCard key={plan.id}>
                <PlanContent>
                  <PlanInfoSection>
                    <PlanTitle>{plan.title}</PlanTitle>
                    <PlanDetails>
                      <PlanTag tagColor={plan.tagColor}>{plan.tag}</PlanTag>
                      <PlanInfo>
                        {plan.price} {plan.nextPayment}
                      </PlanInfo>
                    </PlanDetails>
                  </PlanInfoSection>

                  <ActionButtons>
                    <ChangePlanButton onClick={() => handlePlanChange(plan.id)}>
                      플랜 변경
                    </ChangePlanButton>
                    <UnsubscribeButton
                      onClick={() => handleUnsubscribe(plan.id)}
                    >
                      구독 해지
                    </UnsubscribeButton>
                  </ActionButtons>
                </PlanContent>
              </PlanCard>
            ))}
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default SubscriptionPlansCard;
